/**
 * FO-018 (batch-14) - uniforme reistijd-weergave "Xu Ym".
 *
 * Reistijd wordt overal in MINUTEN opgeslagen (Int op DayEntry/consignatie) of
 * als decimale uren afgeleid (payroll-`reistijdUren`, berekende reistijd). Deze
 * helpers tonen die waarde consistent als "Xu Ym" (bijv. "1u 30m", "45m", "2u")
 * - op planning, werkbon, payroll-overzicht en in de portal. De weekstaat past
 * dezelfde formatter toe via de verloning-stream. De opslag blijft minuten/uren;
 * dit is puur weergave.
 *
 * Bewust "m" (niet "min") conform FO-018 ("1u 30m"). Minuten worden afgerond op
 * de dichtstbijzijnde gehele minuut; negatieve of niet-eindige waarden klampen
 * op "0m".
 */

/**
 * Format een reistijd in MINUTEN naar "Xu Ym".
 *
 *   0    -> "0m"
 *   15   -> "15m"
 *   45   -> "45m"
 *   60   -> "1u"
 *   90   -> "1u 30m"
 *   125  -> "2u 5m"
 */
export function formatReisMinuten(minuten: number | null | undefined): string {
  if (minuten == null || !Number.isFinite(minuten) || minuten <= 0) return '0m'
  const totaal = Math.round(minuten)
  const u = Math.floor(totaal / 60)
  const m = totaal % 60
  if (u === 0) return `${m}m`
  if (m === 0) return `${u}u`
  return `${u}u ${m}m`
}

/**
 * Zelfde "Xu Ym"-weergave, maar voor een reistijd die als DECIMALE UREN in de
 * hand is (bijv. een payroll-`reistijdUren`-Decimal of een berekende
 * `berekendeReistijdUren`). Converteert eerst naar minuten zodat de uitkomst
 * exact gelijk is aan `formatReisMinuten`.
 *
 *   1.5   -> "1u 30m"
 *   0.75  -> "45m"
 *   2     -> "2u"
 */
export function formatReisUren(uren: number | null | undefined): string {
  if (uren == null || !Number.isFinite(uren) || uren <= 0) return '0m'
  return formatReisMinuten(Math.round(uren * 60))
}

/**
 * FO-305 P1 (batch-33) — DE gedeelde decimale-uren-formatter (nl-NL, komma, 2
 * decimalen). Reistijd/reisuren én overige uren-cellen tonen app-breed
 * `#.##0,00` uren i.p.v. de "Xu Ym"-notatie (die vervalt ALS CELWAARDE; een
 * tooltip mag 'Xu Ym' als hulptekst houden via `formatReisMinuten`). 0/leeg/
 * ongeldig -> "0,00" (nooit een lege cel).
 *
 *   0      -> "0,00"
 *   0.25   -> "0,25"
 *   7.5    -> "7,50"
 *   12.5   -> "12,50"
 */
const UREN_NL_FORMAT = new Intl.NumberFormat('nl-NL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
export function formatUren(uren: number | null | undefined): string {
  if (uren == null || !Number.isFinite(uren)) return UREN_NL_FORMAT.format(0)
  return UREN_NL_FORMAT.format(uren)
}
